export const isElementHitBottom = (el: Element, offset = 0) => {
  return Math.floor(el?.scrollHeight - el?.clientHeight - el.scrollTop) <= offset
}

/**
 * 页面 load 完成后、在主线程空闲时执行 fn（最迟 timeout 毫秒后强制执行），返回取消函数。
 * 用于把非首屏必需的重任务（指纹、监控/统计 SDK）挪出 hydration 阶段，
 * 避免与用户的早期点击抢主线程（INP）以及与首屏渲染抢 CPU（LCP）。
 */
export function runWhenIdle(fn: () => void, timeout = 3000): () => void {
  if (typeof window === 'undefined') return () => { }
  let cancelled = false
  let idleId: number | undefined
  let timer: ReturnType<typeof setTimeout> | undefined

  const run = () => { if (!cancelled) fn() }
  const schedule = () => {
    if (cancelled) return
    // Safari 不支持 requestIdleCallback，退化为短延时
    if (typeof window.requestIdleCallback === 'function') idleId = window.requestIdleCallback(run, { timeout })
    else timer = setTimeout(run, 300)
  }

  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })

  return () => {
    cancelled = true
    window.removeEventListener('load', schedule)
    if (idleId !== undefined) window.cancelIdleCallback?.(idleId)
    if (timer !== undefined) clearTimeout(timer)
  }
}

// 用户最近一次输入距今不足该值视为"正在交互"，后台任务继续等待
const QUIET_MS = 2000
// 相邻两个后台任务的最小间隔，避免背靠背执行形成连续长任务
const GAP_MS = 1000
// 从加载完成起最长等待，之后不再等待用户安静（保证最终执行）
const MAX_WAIT_MS = 30000

// 耗时低于该值的任务视为轻量，之后不必间隔 GAP_MS
const LIGHT_TASK_MS = 30

let lastInputAt = -Infinity
let inputTracked = false
const quietQueue: Array<{ fn: () => void | Promise<unknown>, cancelled: boolean }> = []
let draining = false

const trackInput = () => {
  if (inputTracked) return
  inputTracked = true
  const mark = (at = performance.now()) => { lastInputAt = Math.max(lastInputAt, at) }
  for (const type of ['pointerdown', 'keydown']) {
    window.addEventListener(type, () => mark(), { capture: true, passive: true })
  }
  // 监听注册于 hydration 之后，用 buffered 的 Event Timing 补上此前（页面尚不可交互时）的点击
  try {
    new PerformanceObserver(list => {
      for (const e of list.getEntries()) {
        if (['pointerdown', 'mousedown', 'keydown', 'click', 'touchstart'].includes(e.name)) mark(e.startTime)
      }
    }).observe({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit)
    for (const e of performance.getEntriesByType('first-input')) mark(e.startTime)
  } catch { }
}

const drainQuietQueue = () => {
  const next = () => {
    while (quietQueue.length && quietQueue[0].cancelled) quietQueue.shift()
    if (!quietQueue.length) {
      draining = false
      return
    }
    const sinceInput = performance.now() - lastInputAt
    if (sinceInput < QUIET_MS && performance.now() < MAX_WAIT_MS) {
      setTimeout(next, QUIET_MS - sinceInput)
      return
    }
    runWhenIdle(() => {
      // 等待空闲期间用户又开始交互：放回去重新等
      if (performance.now() - lastInputAt < QUIET_MS && performance.now() < MAX_WAIT_MS) {
        next()
        return
      }
      const task = quietQueue.shift()
      if (!task || task.cancelled) {
        next()
        return
      }
      const start = performance.now()
      // 异步任务（如指纹计算）等其完成再计间隔；任务出错不能阻断后续任务
      let result: void | Promise<unknown> = undefined
      try { result = task.fn() } catch (e) { console.error(e) }
      Promise.resolve(result).catch(e => console.error(e)).finally(() => {
        setTimeout(next, performance.now() - start < LIGHT_TASK_MS ? 0 : GAP_MS)
      })
    })
  }
  next()
}

/**
 * 比 runWhenIdle 更保守：页面 load 后，等用户安静（近 QUIET_MS 内无点击/按键）
 * 且主线程空闲时才执行，多个任务串行、彼此间隔 GAP_MS。
 * 适用于数百毫秒级、对时效不敏感的后台任务（设备指纹、统计 SDK 等），
 * 避免它们在页面刚可交互时背靠背执行，恰好撞上用户的首次点击（INP）。
 * fn 返回 Promise 时等其完成后再调度下一个任务。返回取消函数。
 */
export function runWhenQuiet(fn: () => void | Promise<unknown>): () => void {
  if (typeof window === 'undefined') return () => { }
  trackInput()
  const task = { fn, cancelled: false }
  quietQueue.push(task)
  if (!draining) {
    draining = true
    runWhenIdle(drainQuietQueue)
  }
  return () => { task.cancelled = true }
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert blob to base64'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function base64ToBlob(base64: string): Blob {
  const [meta, data] = base64.split(",")
  const mime = meta.match(/:(.*?);/)?.[1] || "application/octet-stream"
  const binaryStr = atob(data)
  const len = binaryStr.length
  const u8arr = new Uint8Array(len)

  for (let i = 0; i < len; i++) {
    u8arr[i] = binaryStr.charCodeAt(i)
  }

  return new Blob([u8arr], { type: mime })
}

// 对图片进行裁剪
export function clipImage(img: string | HTMLImageElement, position): Promise<string> {

  const clip = (imgEl) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const width = position[2] - position[0]
    const height = position[3] - position[1]
    canvas.width = width
    canvas.height = height
    ctx?.drawImage(imgEl, position[0], position[1], width, height, 0, 0, width, height)
    const base64 = canvas.toDataURL()
    return base64
  }

  return new Promise((resolve,) => {
    let imgEl: HTMLImageElement
    if (typeof img === 'string') {
      imgEl = document.createElement('img')
      imgEl.src = img
      imgEl.crossOrigin = "anonymous"
    } else
      imgEl = img
    if (imgEl.complete) {
      resolve(clip(imgEl))
    } else {
      imgEl.onload = () => {
        resolve(clip(imgEl))
      }
    }



  })
}

// 将url转换为File
export async function urlToFile(url: string, fileName: string): Promise<File> {
  const res = await fetch(url);
  const blob = await res.blob();
  const contentType = res.headers.get("Content-Type") || blob.type || "application/octet-stream";

  return new File([blob], fileName, {
    type: contentType,
    lastModified: Date.now(),
  });
}

// 下载文件
export function downloadByUrl(url: string, filename?: string) {
  const link = document.createElement('a')
  link.href = url

  // 指定下载的文件名（可选）
  if (filename) {
    link.download = filename
  } else {
    // 默认用 URL 的最后一段
    link.download = url.split('/').pop() || 'download'
  }

  // 模拟点击触发下载
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * 复制文本，必须在用户点击的同步调用栈里执行。
 * 优先用 execCommand：它是同步的，紧接着跳转页面也不会丢；且 TikTok / Facebook 等 App 内置浏览器
 * 常常没有或禁用了 navigator.clipboard。失败时再退回异步的 Clipboard API
 */
export function copyText(text: string): boolean {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  // 字号 >= 16px 避免 iOS 选中时自动缩放页面
  textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px;'
  document.body.appendChild(textarea)
  textarea.select()
  textarea.setSelectionRange(0, text.length)

  let copied = false
  try {
    copied = document.execCommand('copy')
  } catch { }
  document.body.removeChild(textarea)

  if (!copied) navigator.clipboard?.writeText(text).catch(() => { })
  return copied
}
